import React, { useState, useEffect } from 'react';
import { useTenancy } from '@/contexts/TenancyContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronRight, Info, FileText } from 'lucide-react';
import { toLocalDateString, cleanDateAndTimeObject } from '@shared/renderDate';

interface TimestampLike {
  seconds?: number;
  nanoseconds?: number;
  toDate?: () => Date;
  __time__?: string;
}

interface RiskEditHistoryItem {
  changeID: number;
  changedBy: string;
  dateChanged: any;
  fieldsChanged: Record<string, any>;
  [key: string]: any;
}

interface RiskReviewHistoryItem {
  [key: string]: any;
  reviewDate?: TimestampLike;
  reviewedBy?: string;
  scheduledDate?: TimestampLike;
  evidence?: string | string[];
  comments?: string;
  lastReviewDate?: TimestampLike;
  nextReviewDate?: TimestampLike;
  riskRating?: string;
  treatmentRequired?: string;
}

interface RiskHistoryDialogProps {
  editHistory: Record<string, RiskEditHistoryItem>;
  reviewHistory: Record<string, RiskReviewHistoryItem>;
  isOpen: boolean;
  onClose: () => void;
}

// Robust date formatter for all weird shapes
const formatAnyDate = (date: any): string => {
  if (!date) return 'Unknown date';
  // Firestore __time__ object
  if (typeof date === 'object' && date.__time__ !== undefined) {
    if (!date.__time__) return 'No date recorded';
    const d = new Date(date.__time__);
    if (!isNaN(d.getTime())) return toLocalDateString(d);
    return 'Unknown date';
  }
  // Firebase Timestamp
  if (typeof date === 'object' && date.toDate && typeof date.toDate === 'function') {
    const d = date.toDate();
    if (!isNaN(d.getTime())) return toLocalDateString(d);
    return 'Unknown date';
  }
  // Timestamp-like object
  if (typeof date === 'object' && date.seconds && date.nanoseconds) {
    const d = new Date(date.seconds * 1000 + Math.floor(date.nanoseconds / 1e6));
    if (!isNaN(d.getTime())) return toLocalDateString(d);
    return 'Unknown date';
  }
  // ISO string or readable string
  if (typeof date === 'string') {
    const d = new Date(date);
    if (!isNaN(d.getTime())) return toLocalDateString(d);
    if (/\w+ \d{1,2}, \d{4}/.test(date) || /\d{1,2}\/\d{1,2}\/\d{2,4}/.test(date) || /\d{4}-\d{2}-\d{2}/.test(date)) {
      return date;
    }
    return 'Unknown date';
  }
  // Number (epoch ms or seconds)
  if (typeof date === 'number') {
    let d;
    if (date < 1e12) d = new Date(date * 1000);
    else d = new Date(date);
    if (!isNaN(d.getTime())) return toLocalDateString(d);
    return 'Unknown date';
  }
  try {
    const d = new Date(date);
    if (!isNaN(d.getTime())) return toLocalDateString(d);
  } catch { }
  return 'Unknown date';
};

// Deep equals for objects/arrays
const deepEqual = (a: any, b: any): boolean => {
  if (a === b) return true;
  if (typeof a !== typeof b) return false;
  if (typeof a !== 'object' || a == null || b == null) return false;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((v, i) => deepEqual(v, b[i]));
  }
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every(k => deepEqual(a[k], b[k]));
};

const RiskHistoryDialog: React.FC<RiskHistoryDialogProps> = ({ editHistory, reviewHistory, isOpen, onClose }) => {
  const { userIdToName, users } = useTenancy();
  const [expanded, setExpanded] = useState<string[]>([]);
  const [expandedSections, setExpandedSections] = useState({
    editHistory: true,
    reviewHistory: true
  });

  // Reset expanded states when dialog is closed or when risk changes
  useEffect(() => {
    setExpanded([]);
    setExpandedSections({
      editHistory: true,
      reviewHistory: true
    });
  }, [isOpen, editHistory, reviewHistory]);

  const toggleSection = (section: 'editHistory' | 'reviewHistory') => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Helper to get user display name by id
  const getUserDisplayName = (userId: string) => {
    if (userIdToName[userId]) return userIdToName[userId];
    const user = users.find(u => u.id === userId);
    if (user) return `${user.firstName} ${user.lastName}`;
    return 'Unknown User';
  };

  // Render value for fields
  const renderFieldValue = (field: string, val: any): string => {
    if (!field || typeof field !== 'string') return String(val ?? '');
    const fieldLower = field.toLowerCase();
    if (fieldLower.includes('owner') || fieldLower.includes('assignedto')) {
      return getUserDisplayName(val) || 'Unknown User';
    }
    // Skip rendering delete-related fields
    if (['isdeleted', 'deletedat', 'deletedby'].includes(fieldLower)) {
      return '';
    }
    if (val == null) return '';
    if (typeof val === 'object') {
      if (val.toDate || val.__time__ || val.seconds || val.nanoseconds) {
        return formatAnyDate(val);
      }
      if (val.constructor && val.constructor.name === 'Timestamp') {
        try { return formatAnyDate(val); } catch { }
      }
      try { return JSON.stringify(val); } catch { return '[object Object]'; }
    }
    if (typeof val === 'number' && val > 1000000000) {
      return formatAnyDate(val);
    }
    return String(val);
  };

  // Check if an edit entry represents a delete/restore event
  const getDeleteRestoreEvent = (entry: RiskEditHistoryItem): { type: 'deleted' | 'restored' | null; user: string; date: any } | null => {
    if (!entry.fieldsChanged) return null;
    
    const isDeletedField = Object.entries(entry.fieldsChanged).find(([field, details]) => 
      field && typeof field === 'string' && field.toLowerCase() === 'isdeleted' && !deepEqual(details.oldValue, details.newValue)
    );
    
    if (!isDeletedField) return null;
    
    const [, details] = isDeletedField;
    const newValue = details.newValue;
    
    if (newValue === true || newValue === 'true') {
      return {
        type: 'deleted',
        user: getUserDisplayName(entry.changedBy) || 'Unknown User',
        date: entry.dateChanged
      };
    } else if (newValue === false || newValue === 'false') {
      return {
        type: 'restored',
        user: getUserDisplayName(entry.changedBy) || 'Unknown User',
        date: entry.dateChanged
      };
    }
    
    return null;
  };

  // Get timestamp in milliseconds
  const getTimestampMs = (dateChanged: any) => {
    if (!dateChanged) return 0;
    if (typeof dateChanged === 'object' && typeof dateChanged.seconds === 'number') {
      return dateChanged.seconds * 1000 + Math.floor(dateChanged.nanoseconds / 1e6);
    }
    if (typeof dateChanged === 'number') {
      return dateChanged < 1e12 ? dateChanged * 1000 : dateChanged;
    }
    if (typeof dateChanged === 'string') {
      const d = new Date(dateChanged);
      return isNaN(d.getTime()) ? 0 : d.getTime();
    }
    return 0;
  };

  const sortedEditEntries = Object.entries(editHistory)
    .filter(([key]) => key !== 'historyCount')
    .sort((a, b) => (cleanDateAndTimeObject(b[1]?.dateChanged, true).getTime() || getTimestampMs(b[1]?.dateChanged)) - (cleanDateAndTimeObject(a[1]?.dateChanged, true).getTime() || getTimestampMs(a[1]?.dateChanged)));

  // Convert reviewHistory to an array of entries
  const reviewEntries = Array.isArray(reviewHistory)
    ? reviewHistory.map((item, index) => [index.toString(), item])
    : Object.entries(reviewHistory);

  const getReviewTimestampMs = (dateObj: any) => {
    if (!dateObj) return 0;
    const cleaned = cleanDateAndTimeObject(dateObj, true);
    if (cleaned && typeof cleaned.getTime === 'function') {
      const t = cleaned.getTime();
      if (!isNaN(t)) return t;
    }
    return getTimestampMs(dateObj);
  };

  // Helper to detect 'empty' review entries
  const isEmptyReviewEntry = (entry: any) => {
    const emptyStringFields = [
      entry.comments,
      entry.evidence,
      entry.reviewedBy
    ].every(v => v === '' || v == null || (Array.isArray(v) && v.length === 0));
    const reviewDate = entry.reviewDate;
    const isEpoch = reviewDate && typeof reviewDate.seconds === 'number' && reviewDate.seconds < 2000000000;
    return emptyStringFields && isEpoch;
  };

  const sortedReviewEntries = reviewEntries
    .filter(([key, entry]) => key !== 'historyCount' && !isEmptyReviewEntry(entry))
    .map(([key, entry]) => {
      const reviewDate = entry.reviewDate;
      const sortDate = reviewDate ? getReviewTimestampMs(reviewDate) : 0;
      return { key, entry, sortDate };
    })
    .sort((a, b) => b.sortDate - a.sortDate)
    .map(({ key, entry }) => [key, entry]);

  const toggleExpand = (key: string, type: 'edit' | 'review' = 'edit') => {
    const fullKey = `${type}-${key}`;
    setExpanded(prev => prev.includes(fullKey) ? prev.filter(k => k !== fullKey) : [...prev, fullKey]);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent onOpenAutoFocus={(e) => e.preventDefault()} className={`max-w-3xl! w-full max-h-[90vh] overflow-y-auto rounded-xl shadow-lg border border-white/10 p-0!`} aria-describedby={undefined}>
        <div className="flex flex-col gap-4 h-full bg-card overflow-hidden">
          <div className="sticky top-0 z-20 p-4 py-6 border-b border-white/10 bg-linear-to-b from-primary/5 to-transparent backdrop-blur supports-backdrop-filter:bg-white/60">
            <DialogTitle>Risk History</DialogTitle>
            <DialogDescription>
              View the review history and edit history for this risk.
            </DialogDescription>
          </div>

          <div className="space-y-4 w-full flex-1 overflow-hidden px-4">
            {/* Edit History Section */}
            <div className="border rounded-md overflow-hidden">
              <button
                onClick={() => toggleSection('editHistory')}
                className="w-full text-left bg-primary/15 dark:bg-primary/20 text-primary p-3 font-medium text-sm flex justify-between items-center hover:bg-primary/20 dark:hover:bg-primary/30 transition-colors"
                aria-expanded={expandedSections.editHistory}
              >
                <span>Edit History</span>
                {expandedSections.editHistory ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </button>

              {sortedEditEntries.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No edit history available for this risk.
                </div>
              ) : expandedSections.editHistory ? (
                <div className="flex flex-col">
                  <div className="divide-y">
                    {sortedEditEntries.map(([key, entry]) => {
                      const historyEntry = entry as RiskEditHistoryItem;
                      const user = getUserDisplayName(historyEntry.changedBy) || 'Unknown User';
                      const deleteRestoreEvent = getDeleteRestoreEvent(historyEntry);

                      const entryKeys = Object.keys(historyEntry);
                      const hasNoChanges = (
                        entryKeys.length === 1 ||
                        (entryKeys.length === 2 && historyEntry.changedBy)
                      );

                      return (
                        <div key={`edit-${key}`} className="p-3 hover:bg-primary/5 dark:hover:bg-primary/10">
                          <div
                            className="flex justify-between items-start mb-2 cursor-pointer"
                            onClick={() => toggleExpand(key, 'edit')}
                          >
                            <div className="flex items-center">
                              {expanded.includes(`edit-${key}`) ?
                                <ChevronDown className="h-4 w-4 mr-2" /> :
                                <ChevronRight className="h-4 w-4 mr-2" />
                              }
                              <div>
                                <div className="font-medium">{formatAnyDate(historyEntry.dateChanged)}</div>
                                <div className="text-xs text-gray-500">{user}</div>
                              </div>
                            </div>
                            {deleteRestoreEvent && (
                              <div className={`px-2 py-1 rounded text-xs font-medium ${
                                deleteRestoreEvent.type === 'deleted' 
                                  ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400' 
                                  : 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                              }`}>
                                {deleteRestoreEvent.type === 'deleted' ? '🗑️ Deleted' : '↩️ Restored'}
                              </div>
                            )}
                          </div>
                          {expanded.includes(`edit-${key}`) && (
                            <div className="space-y-2 text-sm mt-2 pl-6 border-l-2 border-primary/30 dark:border-primary/50">
                              {hasNoChanges && !deleteRestoreEvent && (
                                <div className="flex items-start gap-2">
                                  <Info className="h-4 w-4 text-primary/60 mt-0.5" />
                                  <div>
                                    <span className="text-gray-600 dark:text-gray-400 italic">Risk was accessed but no specific changes were recorded</span>
                                  </div>
                                </div>
                              )}
                              {deleteRestoreEvent && (
                                <div className={`flex items-start gap-2 p-3 rounded-md ${
                                  deleteRestoreEvent.type === 'deleted'
                                    ? 'bg-red-50 border border-red-200 dark:bg-red-900/10 dark:border-red-800'
                                    : 'bg-green-50 border border-green-200 dark:bg-green-900/10 dark:border-green-800'
                                }`}>
                                  <div className="shrink-0">
                                    {deleteRestoreEvent.type === 'deleted' ? (
                                      <span className="text-red-600 text-lg">🗑️</span>
                                    ) : (
                                      <span className="text-green-600 text-lg">↩️</span>
                                    )}
                                  </div>
                                  <div className="flex-1">
                                    <div className={`font-semibold ${
                                      deleteRestoreEvent.type === 'deleted' ? 'text-red-800 dark:text-red-300' : 'text-green-800 dark:text-green-300'
                                    }`}>
                                      {deleteRestoreEvent.type === 'deleted' ? 'Risk Deleted' : 'Risk Restored'}
                                    </div>
                                    <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                      {deleteRestoreEvent.type === 'deleted' 
                                        ? `This risk was marked as deleted and hidden from the main list.`
                                        : `This risk was restored and is now active in the main list.`
                                      }
                                    </div>
                                  </div>
                                </div>
                              )}
                              {!deleteRestoreEvent && historyEntry.fieldsChanged && Object.entries(historyEntry.fieldsChanged).filter(([field, details]) => !deepEqual(details.oldValue, details.newValue) && field && typeof field === 'string' && !['isdeleted', 'deletedat', 'deletedby'].includes(field.toLowerCase())).length > 0 && (
                                <div>
                                  <div className="font-semibold mb-1">Fields Changed:</div>
                                  <ul className="list-disc ml-5">
                                    {Object.entries(historyEntry.fieldsChanged)
                                      .filter(([field, details]) => !deepEqual(details.oldValue, details.newValue) && field && typeof field === 'string' && !['isdeleted', 'deletedat', 'deletedby'].includes(field.toLowerCase()))
                                      .map(([field, details]) => (
                                        <li key={field}>
                                          <span className="font-semibold">{details.fieldName || field}:</span>{' '}
                                          <span className="text-red-600 line-through">{renderFieldValue(details.dbFieldName, details.oldValue)}</span> → {' '}
                                          <span className="text-green-600">{renderFieldValue(details.dbFieldName, details.newValue)}</span>
                                        </li>
                                      ))}
                                  </ul>
                                </div>
                              )}
                              {!deleteRestoreEvent && historyEntry.fieldsChanged && Object.entries(historyEntry.fieldsChanged).filter(([field, details]) => !deepEqual(details.oldValue, details.newValue) && field && typeof field === 'string' && !['isdeleted', 'deletedat', 'deletedby'].includes(field.toLowerCase())).length === 0 && (
                                <div className="flex items-start gap-2">
                                  <Info className="h-4 w-4 text-primary/60 mt-0.5" />
                                  <div>
                                    <span className="text-gray-600 dark:text-gray-400 italic">No actual changes recorded in this entry</span>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </div>

            {/* Review History Section */}
            <div className="border rounded-md overflow-hidden">
              <button
                onClick={() => toggleSection('reviewHistory')}
                className="w-full text-left bg-primary/15 dark:bg-primary/20 text-primary p-3 font-medium text-sm flex justify-between items-center hover:bg-primary/20 dark:hover:bg-primary/30 transition-colors"
                aria-expanded={expandedSections.reviewHistory}
              >
                <span>Review History</span>
                {expandedSections.reviewHistory ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </button>

              {sortedReviewEntries.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No review history available for this risk.
                </div>
              ) : expandedSections.reviewHistory ? (
                <div className="flex flex-col">
                  <div className="divide-y">
                    {sortedReviewEntries.map(([key, entry]: [string, RiskReviewHistoryItem]) => {
                      const latestDate = entry.reviewDate || entry.lastReviewDate || null;
                      const user = entry.reviewedBy ? getUserDisplayName(entry.reviewedBy) : 'System';
                      const dateStr = latestDate ? formatAnyDate(latestDate) : 'No date';

                      return (
                        <div key={`review-${key}`} className="p-3 hover:bg-primary/5 dark:hover:bg-primary/10">
                          <div
                            className="flex justify-between items-start mb-2 cursor-pointer"
                            onClick={() => toggleExpand(key, 'review')}
                          >
                            <div className="flex items-center">
                              {expanded.includes(`review-${key}`) ?
                                <ChevronDown className="h-4 w-4 mr-2" /> :
                                <ChevronRight className="h-4 w-4 mr-2" />
                              }
                              <div>
                                <div className="font-medium">
                                  {entry.reviewDate ? formatAnyDate(entry.reviewDate) : dateStr}
                                  {entry.reviewDate && entry.scheduledDate && (
                                    <span className="ml-2 text-xs text-gray-500">
                                      (scheduled: {formatAnyDate(entry.scheduledDate)})
                                    </span>
                                  )}
                                </div>
                                <div className="text-xs text-gray-500">{user}</div>
                              </div>
                            </div>
                          </div>
                          {expanded.includes(`review-${key}`) && (
                            <div className="space-y-2 text-sm mt-2 pl-6 border-l-2 border-primary/30 dark:border-primary/50">
                              {/* Display dates */}
                              <div className="grid grid-cols-2 gap-2">
                                {entry.lastReviewDate && (
                                  <div>
                                    <div className="text-xs text-gray-500">Last Review:</div>
                                    <div>{formatAnyDate(entry.lastReviewDate)}</div>
                                  </div>
                                )}
                                {entry.nextReviewDate && (
                                  <div>
                                    <div className="text-xs text-gray-500">Next Review:</div>
                                    <div>{formatAnyDate(entry.nextReviewDate)}</div>
                                  </div>
                                )}
                                {entry.reviewDate && (
                                  <div>
                                    <div className="text-xs text-gray-500">Reviewed:</div>
                                    <div>{formatAnyDate(entry.reviewDate)}</div>
                                  </div>
                                )}
                                {entry.scheduledDate && (
                                  <div>
                                    <div className="text-xs text-gray-500">Scheduled:</div>
                                    <div>{formatAnyDate(entry.scheduledDate)}</div>
                                  </div>
                                )}
                              </div>

                              {/* Display risk-specific fields */}
                              {entry.riskRating && (
                                <div className="mt-2">
                                  <div className="font-semibold">Risk Rating:</div>
                                  <div className="text-gray-700 dark:text-gray-300">{entry.riskRating}</div>
                                </div>
                              )}

                              {entry.treatmentRequired && (
                                <div className="mt-2">
                                  <div className="font-semibold">Treatment Required:</div>
                                  <div className="text-gray-700 dark:text-gray-300">{entry.treatmentRequired}</div>
                                </div>
                              )}

                              {/* Display additional fields */}
                              {entry.comments && (
                                <div className="mt-2">
                                  <div className="font-semibold">Comments:</div>
                                  <div className="text-gray-700 dark:text-gray-300">{entry.comments}</div>
                                </div>
                              )}

                              {entry.evidence && entry.evidence !== 'None' && (
                                <div className="mt-2">
                                  <div className="font-semibold">Evidence:</div>
                                  <div className="text-gray-700 dark:text-gray-300 flex flex-col gap-1">
                                    {Array.isArray(entry.evidence) ? (
                                      entry.evidence.map((ev, idx) => (
                                        <span key={ev + idx} className="flex items-center gap-1">
                                          <FileText className="inline-block h-4 w-4" />
                                          {ev.split('/').pop() || `evidence_${idx + 1}`}
                                        </span>
                                      ))
                                    ) : (
                                      <span className="flex items-center gap-1">
                                        <FileText className="inline-block h-4 w-4" />
                                        {entry.evidence.split('/').pop() || 'evidence'}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          <DialogFooter className="mt-4 p-4 sticky bottom-0 z-20 border-white/10 bg-linear-to-t from-primary/5 to-transparent backdrop-blur supports-backdrop-filter:bg-white/60 border-t flex! flex-row! justify-between! items-center! align-center! gap-2">
            <Button variant="outline" onClick={onClose} className="w-full text-center justify-center">
              Close
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RiskHistoryDialog;
